import {
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@material-ui/core';
import { useEffect } from 'react';

import { usePlacesAutocompleteBackend, useGetLocationData } from './hooks';

import makeClasses from './InputPlacesAutocompleteBackend.styles';

export function InputPlacesAutocompleteBackend({
  defaultValue = '',
  placeholder = '',
  onChange,
  error = false,
  disabled = false,
  label,
  required = false,
  variant = 'outlined',
  fullWidth = true,
  helperText,
  isTouched = false,
  onBlur,
}: Props) {
  const {
    ready,
    value,
    suggestions,
    setValue,
    clearSuggestions,
  } = usePlacesAutocompleteBackend({
    debounce: 300,
    requestOptions: { maxResults: 5 },
  });

  const { getLocationData } = useGetLocationData();

  useEffect(() => {
    if (defaultValue && !value) {
      setValue(defaultValue, false);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (disabled && defaultValue) {
      setValue(defaultValue, false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!value && !disabled && isTouched && onChange) {
      onChange(null);
    }
  }, [value, disabled, isTouched]);

  function handleInput(e) {
    if (!e) return;
    const {
      target: { value: inputValue },
    } = e;
    setValue(inputValue);
  }

  async function handleSelect(suggestion) {
    clearSuggestions();

    try {
      const locationData = await getLocationData(
        suggestion.PlaceId,
        suggestion.Text,
      );

      if (locationData) {
        setValue(suggestion.Text, false);
        onChange?.(locationData);
      } else {
        console.error(
          'Failed to get location data for place:',
          suggestion.PlaceId,
        );
        setValue(suggestion.Text, false);
        onChange?.({
          completeAddress: suggestion.Text,
          parseAddress: suggestion.Text,
          coordinates: { lat: 0, lng: 0 },
          city: '',
        });
      }
    } catch (error) {
      console.error('Error selecting place:', error);
      setValue(suggestion.Text, false);
      onChange?.({
        completeAddress: suggestion.Text,
        parseAddress: suggestion.Text,
        coordinates: { lat: 0, lng: 0 },
        city: '',
      });
    }
  }

  function renderSuggestions() {
    if (disabled) return null;
    return (suggestions?.data || []).map(suggestion => {
      const mainText = suggestion.Text.split(',')[0] || suggestion.Text;
      const secondaryText = suggestion.Text.split(',')
        .slice(1)
        .join(',')
        .trim();
      return (
        <ListItem
          button
          key={suggestion.PlaceId}
          className={_classes.placeSuggestion}
          onClick={() => handleSelect(suggestion)}
        >
          <ListItemText
            primary={mainText}
            secondary={secondaryText || undefined}
          />
        </ListItem>
      );
    });
  }

  const _classes = makeClasses({ disabled });

  if (!ready) return null;
  return (
    <Box position='relative'>
      <TextField
        classes={{ root: _classes.disabled }}
        value={value}
        onChange={handleInput}
        placeholder={placeholder}
        disabled={disabled || !ready}
        error={error}
        label={label}
        required={required}
        variant={variant}
        fullWidth={fullWidth}
        helperText={helperText}
        onBlur={onBlur}
      />
      {suggestions?.status === 'OK' && (
        <List
          className={_classes.placesSuggestions}
          aria-label='places-suggestions'
        >
          {renderSuggestions()}
        </List>
      )}
    </Box>
  );
}

interface Props {
  defaultValue?: string;
  placeholder?: string;
  onChange: any;
  error?: boolean;
  disabled?: boolean;
  label: string;
  required?: boolean;
  variant: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  helperText?: any;
  isTouched?: boolean;
  onBlur?: any;
}
